import React, { useState, useEffect } from 'react';
import config from './config';

const InventionAssessmentPage = ({ user, onBack, setPage, setShowLoginPrompt, setLoginPromptMessage, onUsageUpdate }) => {
  const [invention, setInvention] = useState({
    title: '',
    description: '',
    novelty: '',
    features: '',
    field: '',
    priority_date: new Date().toISOString().split('T')[0]
  });
  
  const [assessments, setAssessments] = useState({
    patentability: null,
    fto: null,
    landscape: null,
    priorArt: null,
    competitive: null
  });
  
  const [activeAssessment, setActiveAssessment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportHtml, setReportHtml] = useState(null);
  const [reportPapers, setReportPapers] = useState([]);
  const [suggestingField, setSuggestingField] = useState(false);
  const [suggestedFields, setSuggestedFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showLocalPrompt, setShowLocalPrompt] = useState(false);
  const [localPromptMessage, setLocalPromptMessage] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load saved state on mount - only for current user
  useEffect(() => {
    if (user?.email) {
      const saved = localStorage.getItem(`invention_assessment_${user.email}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setInvention(parsed.invention || {
            title: '',
            description: '',
            novelty: '',
            features: '',
            field: '',
            priority_date: new Date().toISOString().split('T')[0]
          });
          setAssessments(parsed.assessments || {
            patentability: null,
            fto: null,
            landscape: null,
            priorArt: null,
            competitive: null
          });
        } catch (e) {
          console.error('Error loading saved assessment:', e);
        }
      } else {
        // No saved data for this user - start fresh
        setInvention({
          title: '',
          description: '',
          novelty: '',
          features: '',
          field: '',
          priority_date: new Date().toISOString().split('T')[0]
        });
        setAssessments({
          patentability: null,
          fto: null,
          landscape: null,
          priorArt: null,
          competitive: null
        });
      }
      setReportHtml(null);
      setError('');
    }
  }, [user?.email]);
  
  // Listen for Prior Art navigation from report iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === 'startPriorArt') {
        // Save invention data for Prior Art page
        localStorage.setItem('prior_art_prefill', JSON.stringify({
          title: invention.title,
          description: invention.description,
          novelty: invention.novelty,
          features: invention.features,
          field: invention.field
        }));
        setReportHtml(null);
        setPage('prior-art');
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [invention, setPage]);

  // Save state on changes - user-specific
  const saveState = (newInvention, newAssessments) => {
    if (user?.email) {
      localStorage.setItem(`invention_assessment_${user.email}`, JSON.stringify({
        invention: newInvention || invention,
        assessments: newAssessments || assessments
      }));
    }
  };

  // Suggest technical fields using AI
  const suggestFields = async () => {
    if (!invention.title && !invention.description) {
      setError('Please enter a title or description first');
      return;
    }
    
    setSuggestingField(true);
    setSuggestedFields([]);
    
    try {
      const response = await fetch(`${config.API_URL}/api/suggest-field`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: invention.title,
          description: invention.description
        })
      });
      
      const data = await response.json();
      if (data.success && data.fields) {
        setSuggestedFields(data.fields);
      } else {
        setError(data.error || 'Could not suggest fields');
      }
    } catch (err) {
      setError('Error suggesting fields');
    } finally {
      setSuggestingField(false);
    }
  };

  const handleInputChange = (field, value) => {
    const newInvention = { ...invention, [field]: value };
    setInvention(newInvention);
    saveState(newInvention, assessments);
  };

  const runAssessment = async (type) => {
    // Check if user is logged in
    if (!user) {
      setLocalPromptMessage('Please log in to run assessments. Free registration gives you access to assessment summaries for each tool.');
      setShowLocalPrompt(true);
      return;
    }

    if (!invention.title.trim() || !invention.description.trim()) {
      setError('Please enter at least a title and description');
      return;
    }

    setActiveAssessment(type);
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      let endpoint, body;

      if (type === 'patentability') {
        endpoint = `${config.API_URL}/api/patentability-check`;
        body = {
          title: invention.title,
          description: invention.description,
          novelty: invention.novelty,
          field: invention.field,
          token
        };
      } else if (type === 'fto') {
        endpoint = `${config.API_URL}/api/fto-assessment`;
        body = {
          product_name: invention.title,
          description: invention.description,
          features: invention.features || invention.novelty,
          market: 'United States',
          token
        };
      } else if (type === 'landscape') {
        endpoint = `${config.API_URL}/api/tech-landscape`;
        body = {
          technology: invention.field || invention.title,
          aspect: '',
          token
        };
      } else if (type === 'priorArt') {
        endpoint = `${config.API_URL}/api/prior-art-deep-dive`;
        body = {
          title: invention.title,
          description: invention.description,
          key_features: invention.features || invention.novelty,
          field: invention.field,
          priority_date: invention.priority_date || new Date().toISOString().split('T')[0],
          token
        };
      } else if (type === 'competitive') {
        endpoint = `${config.API_URL}/api/competitive-intelligence`;
        body = {
          competitors: '',
          technology_area: invention.field || invention.title,
          your_company: '',
          timeframe_years: 5,
          token
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.success) {
        const newAssessments = { ...assessments, [type]: data };
        setAssessments(newAssessments);
        saveState(invention, newAssessments);
      } else {
        setError(data.error || 'Assessment failed');
      }
    } catch (err) {
      setError('Error running assessment');
      console.error(err);
    } finally {
      setLoading(false);
      setActiveAssessment(null);
    }
  };

  const clearAll = () => {
    setInvention({ title: '', description: '', novelty: '', features: '', field: '', priority_date: new Date().toISOString().split('T')[0] });
    setAssessments({ patentability: null, fto: null, landscape: null, priorArt: null, competitive: null });
    localStorage.removeItem('invention_assessment');
  };

  const generateReport = async () => {
    // Check if user has Premium or Pro
    if (user.tier === 'free') {
      setLocalPromptMessage('Combined Assessment Reports require a Premium or Pro subscription. Upgrade to generate comprehensive reports with all 5 assessments combined.');
      setShowLocalPrompt(true);
      return;
    }

    setGeneratingReport(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/invention-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invention, assessments, token })
      });
      
      const data = await response.json();
      if (data.success && data.report_html) {
        setReportHtml(data.report_html);
        setReportPapers(data.papers || []);
        if (onUsageUpdate) onUsageUpdate();
      } else {
        setError(data.error || 'Failed to generate report');
      }
    } catch (err) {
      setError('Error generating report');
    } finally {
      setGeneratingReport(false);
    }
  };
  
  const downloadReport = () => {
    if (!reportHtml) return;
    const blob = new Blob([reportHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (invention.title || 'invention') + '-assessment-report.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const printReport = () => {
    if (!reportHtml) return;
    // Open in new window for print/save as PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(reportHtml);
    printWindow.document.close();
    printWindow.focus();
    // Delay print dialog to ensure content loads
    setTimeout(() => printWindow.print(), 500);
  };

  const downloadPDF = async () => {
    if (!assessments) return;
    try {
      const response = await fetch(`${config.API_URL}/api/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_data: {
            overall_assessment: {
              signal: getOverallSignal(),
              summary: getOverallSummary()
            },
            key_findings: getKeyFindings(),
            recommended_actions: getRecommendations(),
            risk_factors: getRiskFactors()
          },
          form_data: {
            invention: invention,
            assessments: assessments,
            invention_title: invention.title,
            papers: reportPapers
          },
          report_type: 'invention'
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PatTrak_Assessment_${(invention.title || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to generate PDF. Use Print button and Save as PDF instead.');
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Use Print button and Save as PDF instead.');
    }
  };
  
  // Helper functions for PDF generation
  const getOverallSignal = () => {
    if (!assessments.patentability && !assessments.fto) return 'INCOMPLETE';
    const patSignal = assessments.patentability?.signal;
    const ftoRisk = assessments.fto?.overall_risk;
    if (patSignal === 'NEGATIVE' || ftoRisk === 'HIGH') return 'CAUTION';
    if (patSignal === 'POSITIVE' && ftoRisk === 'LOW') return 'FAVORABLE';
    return 'CAUTIOUS';
  };
  
  const getOverallSummary = () => {
    const parts = [];
    if (assessments.patentability?.summary) parts.push(assessments.patentability.summary.substring(0, 200));
    if (assessments.fto?.summary) parts.push(assessments.fto.summary.substring(0, 200));
    return parts.join(' ') || 'Assessment complete.';
  };
  
  const getKeyFindings = () => {
    const findings = [];
    if (assessments.patentability?.novelty_points) {
      assessments.patentability.novelty_points.slice(0, 2).forEach(p => findings.push(p));
    }
    if (assessments.fto?.potentially_blocking_patents) {
      findings.push(`${assessments.fto.potentially_blocking_patents.length} potentially blocking patents identified`);
    }
    if (assessments.landscape?.top_players) {
      findings.push(`Top players: ${assessments.landscape.top_players.slice(0, 3).map(p => p.name).join(', ')}`);
    }
    return findings;
  };
  
  const getRecommendations = () => {
    const recs = [];
    if (assessments.patentability?.concerns?.length > 0) {
      recs.push('Address patentability concerns before filing');
    }
    if (assessments.fto?.overall_risk === 'HIGH') {
      recs.push('Conduct detailed FTO analysis with patent attorney');
    }
    if (assessments.priorArt?.prior_art?.length > 0) {
      recs.push('Review identified prior art references');
    }
    recs.push('Consult with a patent attorney before proceeding');
    return recs;
  };

  const getRiskFactors = () => {
    const risks = [];
    if (assessments.patentability?.concerns) {
      assessments.patentability.concerns.slice(0, 2).forEach(c => risks.push(c));
    }
    if (assessments.fto?.overall_risk === 'HIGH' || assessments.fto?.overall_risk === 'MEDIUM') {
      risks.push('Potential patent infringement risks require further investigation');
    }
    if (assessments.landscape?.total_patents > 100) {
      risks.push('Crowded patent landscape may limit claim scope');
    }
    return risks;
  };

  const saveReport = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to save reports');
      return;
    }

    setSaving(true);
    setSaveSuccess(false);

    try {
      const response = await fetch(`${config.API_URL}/api/saved-invention-assessments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: invention.title || 'Untitled Assessment',
          invention_data: invention,
          assessments_data: assessments,
          report_html: reportHtml,
          token: token
        })
      });

      const data = await response.json();
      if (data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert(data.error || 'Failed to save report');
      }
    } catch (error) {
      console.error('Error saving report:', error);
      alert('Error saving report');
    } finally {
      setSaving(false);
    }
  };

  const completedCount = Object.values(assessments).filter(a => a !== null).length;
  const allComplete = completedCount === 5;
  const canStart = invention.title.trim() && invention.description.trim();

  const getSignalDisplay = (type) => {
    const assessment = assessments[type];
    if (!assessment) return null;

    let signal, label, color;
    if (type === 'patentability') {
      signal = assessment.signal;
      label = signal === 'GREEN' ? 'Promising' : signal === 'CAUTION' ? 'Caution' : 'Concerns';
      color = signal === 'GREEN' ? '#16a34a' : signal === 'CAUTION' ? '#f59e0b' : '#dc2626';
    } else if (type === 'fto') {
      signal = assessment.overall_risk;
      label = signal === 'LOW' ? 'Low Risk' : signal === 'MEDIUM' ? 'Medium Risk' : 'High Risk';
      color = signal === 'LOW' ? '#16a34a' : signal === 'MEDIUM' ? '#f59e0b' : '#dc2626';
    } else if (type === 'landscape') {
      signal = assessment.maturity_stage;
      label = signal;
      color = '#0369a1';
    } else if (type === 'priorArt') {
      signal = assessment.summary?.risk_level || 'MEDIUM';
      label = signal === 'HIGH' ? 'High Risk' : signal === 'MEDIUM' ? 'Medium' : 'Low Risk';
      color = signal === 'HIGH' ? '#dc2626' : signal === 'MEDIUM' ? '#f59e0b' : '#16a34a';
    } else if (type === 'competitive') {
      const compCount = assessment.competitors_analyzed || 0;
      signal = compCount;
      label = `${compCount} Analyzed`;
      color = '#7c3aed';
    }

    return { signal, label, color };
  };

  // Gate for non-logged-in users
  if (!user) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <header style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: '14px', fontWeight: '500' }}>
            ← Back to Search
          </button>
        </header>
        
        <main style={{ maxWidth: '600px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
          <div style={{ 
            background: 'white', 
            borderRadius: '16px', 
            padding: '48px 32px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔬</div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
              Invention Assessment
            </h1>
            <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
              Get AI-powered assessment of your invention across 5 key dimensions: 
              Patentability, Freedom to Operate, Technology Landscape, Prior Art, and Competitive Intelligence.
            </p>
            
            <div style={{ 
              background: '#f0f9ff', 
              border: '1px solid #bae6fd', 
              borderRadius: '12px', 
              padding: '20px',
              marginBottom: '32px'
            }}>
              <p style={{ color: '#0369a1', fontSize: '14px', margin: 0 }}>
                <strong>Free registration</strong> gives you access to assessment summaries for each tool. 
                <strong> Premium and Pro users</strong> can generate comprehensive combined reports.
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '280px', margin: '0 auto' }}>
              <button
                onClick={() => setPage('register')}
                style={{
                  padding: '14px 28px',
                  background: '#0369a1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                Register Free
              </button>
              <button
                onClick={() => setPage('login')}
                style={{
                  padding: '14px 28px',
                  background: 'white',
                  color: '#0369a1',
                  border: '1px solid #0369a1',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                Log In
              </button>
              <button
                onClick={onBack}
                style={{
                  padding: '10px',
                  background: 'none',
                  color: '#64748b',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#0369a1',
            cursor: 'pointer',
            fontSize: '14px',
            padding: 0,
            marginBottom: '16px'
          }}
        >
          ← Back to Search
        </button>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
              Invention Assessment
            </h1>
            <p style={{ color: '#64748b', marginTop: '8px', fontSize: '16px' }}>
              Complete all five assessments to generate your combined invention report
            </p>
          </div>
          {(invention.title || completedCount > 0) && (
            <button
              onClick={clearAll}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #fecaca',
                background: '#fef2f2',
                color: '#dc2626',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Start Over
            </button>
          )}
        </div>
      </div>

      {/* Progress */}
      <div style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontWeight: '500', color: '#374151' }}>Progress</span>
          <span style={{ color: '#64748b' }}>{completedCount} of 5 assessments complete</span>
        </div>
        <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{
            width: `${(completedCount / 5) * 100}%`,
            height: '100%',
            background: allComplete ? '#16a34a' : '#0369a1',
            transition: 'width 0.3s'
          }} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '24px' }}>
        {/* Left: Invention Details */}
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          height: 'fit-content'
        }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '20px' }}>
            Your Invention
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                Title *
              </label>
              <input
                type="text"
                value={invention.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Self-healing concrete"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                Priority Date *
                <span style={{ fontWeight: '400', color: '#64748b', marginLeft: '8px' }}>
                  (for prior art comparison)
                </span>
              </label>
              <input
                type="date"
                value={invention.priority_date}
                onChange={(e) => handleInputChange('priority_date', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                Description *
              </label>
              <textarea
                value={invention.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="What does it do? What problem does it solve?"
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                What's new about it?
              </label>
              <textarea
                value={invention.novelty}
                onChange={(e) => handleInputChange('novelty', e.target.value)}
                placeholder="The key innovation"
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                Key features
              </label>
              <textarea
                value={invention.features}
                onChange={(e) => handleInputChange('features', e.target.value)}
                placeholder="Main components or methods"
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151', fontSize: '13px' }}>
                Technical field
                <span style={{ fontWeight: '400', color: '#64748b', marginLeft: '8px' }}>
                  (be specific - helps find relevant patents)
                </span>
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={invention.field}
                  onChange={(e) => { handleInputChange('field', e.target.value); setSuggestedFields([]); }}
                  placeholder="e.g., drug delivery systems, battery technology"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    outline: 'none'
                  }}
                />
                <button
                  type="button"
                  onClick={suggestFields}
                  disabled={suggestingField || (!invention.title && !invention.description)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '6px',
                    border: '1px solid #0369a1',
                    background: suggestingField ? '#e0f2fe' : 'white',
                    color: '#0369a1',
                    cursor: suggestingField || (!invention.title && !invention.description) ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {suggestingField ? 'Thinking...' : 'Suggest'}
                </button>
              </div>
              {suggestedFields.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {suggestedFields.map((field, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => { handleInputChange('field', field); setSuggestedFields([]); }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        border: '1px solid #0369a1',
                        background: '#f0f9ff',
                        color: '#0369a1',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {field}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {!canStart && (
            <p style={{ fontSize: '12px', color: '#f59e0b', marginTop: '16px' }}>
              Enter a title and description to start assessments
            </p>
          )}
        </div>

        {/* Right: Assessment Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Patentability */}
          <div style={{
            background: 'white',
            border: assessments.patentability ? '2px solid #16a34a' : '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                  1. Patentability Check
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                  Is your invention likely patentable?
                </p>
              </div>
              
              {assessments.patentability ? (
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    background: `${getSignalDisplay('patentability').color}15`,
                    color: getSignalDisplay('patentability').color
                  }}>
                    {getSignalDisplay('patentability').label}
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => runAssessment('patentability')}
                  disabled={!canStart || loading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: !canStart || loading ? '#e2e8f0' : '#0369a1',
                    color: !canStart || loading ? '#94a3b8' : 'white',
                    cursor: !canStart || loading ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  {activeAssessment === 'patentability' ? 'Running...' : 'Run'}
                </button>
              )}
            </div>
            
            {assessments.patentability && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', color: '#475569', margin: 0 }}>
                  {assessments.patentability.summary?.slice(0, 200)}...
                </p>
                <button
                  onClick={() => runAssessment('patentability')}
                  disabled={loading}
                  style={{
                    marginTop: '12px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Run Again
                </button>
              </div>
            )}
          </div>

          {/* FTO */}
          <div style={{
            background: 'white',
            border: assessments.fto ? '2px solid #16a34a' : '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                  2. FTO Assessment
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                  Can you make/sell this without infringing?
                </p>
              </div>
              
              {assessments.fto ? (
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    background: `${getSignalDisplay('fto').color}15`,
                    color: getSignalDisplay('fto').color
                  }}>
                    {getSignalDisplay('fto').label}
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => runAssessment('fto')}
                  disabled={!canStart || loading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: !canStart || loading ? '#e2e8f0' : '#0369a1',
                    color: !canStart || loading ? '#94a3b8' : 'white',
                    cursor: !canStart || loading ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  {activeAssessment === 'fto' ? 'Running...' : 'Run'}
                </button>
              )}
            </div>
            
            {assessments.fto && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', color: '#475569', margin: 0 }}>
                  {assessments.fto.summary?.slice(0, 200)}...
                </p>
                <button
                  onClick={() => runAssessment('fto')}
                  disabled={loading}
                  style={{
                    marginTop: '12px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Run Again
                </button>
              </div>
            )}
          </div>

          {/* Landscape */}
          <div style={{
            background: 'white',
            border: assessments.landscape ? '2px solid #16a34a' : '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                  3. Technology Landscape
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                  Who else is active in this space?
                </p>
              </div>
              
              {assessments.landscape ? (
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    background: `${getSignalDisplay('landscape').color}15`,
                    color: getSignalDisplay('landscape').color
                  }}>
                    {getSignalDisplay('landscape').label}
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => runAssessment('landscape')}
                  disabled={!canStart || loading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: !canStart || loading ? '#e2e8f0' : '#0369a1',
                    color: !canStart || loading ? '#94a3b8' : 'white',
                    cursor: !canStart || loading ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  {activeAssessment === 'landscape' ? 'Running...' : 'Run'}
                </button>
              )}
            </div>
            
            {assessments.landscape && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', color: '#475569', margin: 0 }}>
                  {assessments.landscape.total_patents?.toLocaleString()} patents from {assessments.landscape.unique_assignees} companies. {assessments.landscape.summary?.slice(0, 100)}...
                </p>
                <button
                  onClick={() => runAssessment('landscape')}
                  disabled={loading}
                  style={{
                    marginTop: '12px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Run Again
                </button>
              </div>
            )}
          </div>


          {/* Prior Art Deep Dive */}
          <div style={{
            background: 'white',
            border: assessments.priorArt ? '2px solid #16a34a' : '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                  4. Prior Art Deep Dive
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                  What existing patents are most relevant?
                </p>
              </div>

              {assessments.priorArt ? (
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    background: `${getSignalDisplay('priorArt').color}15`,
                    color: getSignalDisplay('priorArt').color
                  }}>
                    {getSignalDisplay('priorArt').label}
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => runAssessment('priorArt')}
                  disabled={!canStart || loading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: !canStart || loading ? '#e2e8f0' : '#0369a1',
                    color: !canStart || loading ? '#94a3b8' : 'white',
                    cursor: !canStart || loading ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  {activeAssessment === 'priorArt' ? 'Running...' : 'Run'}
                </button>
              )}
            </div>

            {assessments.priorArt && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', color: '#475569', margin: 0 }}>
                  Found {assessments.priorArt.prior_art?.length || 0} relevant prior art references. {assessments.priorArt.summary?.headline?.slice(0, 100)}...
                </p>
                <button
                  onClick={() => runAssessment('priorArt')}
                  disabled={loading}
                  style={{
                    marginTop: '12px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Run Again
                </button>
              </div>
            )}
          </div>

          {/* Competitive Intelligence */}
          <div style={{
            background: 'white',
            border: assessments.competitive ? '2px solid #16a34a' : '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                  5. Competitive Intelligence
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                  Who are the key players in this space?
                </p>
              </div>

              {assessments.competitive ? (
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    background: `${getSignalDisplay('competitive').color}15`,
                    color: getSignalDisplay('competitive').color
                  }}>
                    {getSignalDisplay('competitive').label}
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => runAssessment('competitive')}
                  disabled={!canStart || loading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: !canStart || loading ? '#e2e8f0' : '#0369a1',
                    color: !canStart || loading ? '#94a3b8' : 'white',
                    cursor: !canStart || loading ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  {activeAssessment === 'competitive' ? 'Running...' : 'Run'}
                </button>
              )}
            </div>

            {assessments.competitive && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', color: '#475569', margin: 0 }}>
                  Analyzed {assessments.competitive.total_patents?.toLocaleString() || 0} patents. {assessments.competitive.summary?.headline?.slice(0, 100)}...
                </p>
                <button
                  onClick={() => runAssessment('competitive')}
                  disabled={loading}
                  style={{
                    marginTop: '12px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#475569',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Run Again
                </button>
              </div>
            )}
          </div>

          {/* Generate Report */}
          <div style={{
            background: allComplete ? '#f0fdf4' : '#f8fafc',
            border: allComplete ? '2px solid #16a34a' : '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center'
          }}>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: !allComplete ? '#94a3b8' : user?.tier === 'free' ? '#0369a1' : '#166534',
              marginBottom: '8px'
            }}>
              {allComplete ? (user?.tier === 'free' ? 'Upgrade to Get Full Report' : 'All Assessments Complete!') : 'Combined Report'}
            </h3>
            <p style={{ 
              fontSize: '14px', 
              color: !allComplete ? '#94a3b8' : user?.tier === 'free' ? '#64748b' : '#15803d',
              marginBottom: '16px'
            }}>
              {allComplete 
                ? 'Generate your comprehensive invention assessment report'
                : `Complete ${5 - completedCount} more assessment${5 - completedCount > 1 ? 's' : ''} to generate your report`
              }
            </p>
            <button
              onClick={generateReport}
              disabled={!allComplete || generatingReport}
              style={{
                padding: '14px 32px',
                borderRadius: '8px',
                border: 'none',
                background: !allComplete ? '#e2e8f0' : user?.tier === 'free' ? '#0369a1' : '#16a34a',
                color: allComplete ? 'white' : '#94a3b8',
                cursor: allComplete ? 'pointer' : 'not-allowed',
                fontWeight: '600',
                fontSize: '16px'
              }}
            >
              {generatingReport ? 'Generating...' : user?.tier === 'free' ? 'Upgrade to Generate' : 'Generate Report'}
            </button>
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}
        </div>
      </div>
      
      {/* Report Display Modal */}
      {reportHtml && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                Invention Assessment Report
              </h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={downloadReport}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Download HTML
                </button>
                <button
                  onClick={saveReport}
                  disabled={saving}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    background: saveSuccess ? '#16a34a' : '#0369a1',
                    color: 'white',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Report'}
                </button>
                <button
                  onClick={downloadPDF}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#0369a1',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Download PDF
                </button>
                <button
                  onClick={printReport}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Print
                </button>
                <button
                  onClick={() => setReportHtml(null)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  X
                </button>
              </div>
            </div>
            
            {/* Report Content */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '0'
            }}>
              <iframe
                srcDoc={reportHtml}
                title="Invention Report"
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '500px',
                  border: 'none'
                }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Login/Upgrade Prompt Modal */}
      {showLocalPrompt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
              {user ? 'Upgrade Required' : 'Registration Required'}
            </h3>
            <p style={{ color: '#64748b', marginBottom: '24px', lineHeight: '1.5' }}>
              {localPromptMessage}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {user ? (
                <button
                  onClick={() => {
                    setShowLocalPrompt(false);
                    setPage('pricing');
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#0369a1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '15px'
                  }}
                >
                  {user.tier === 'free' ? 'Upgrade to Premium' : 'Upgrade to Pro'}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowLocalPrompt(false);
                      setPage('register');
                    }}
                    style={{
                      padding: '12px 24px',
                      background: '#0369a1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '15px'
                    }}
                  >
                    Register Free
                  </button>
                  <button
                    onClick={() => {
                      setShowLocalPrompt(false);
                      setPage('login');
                    }}
                    style={{
                      padding: '12px 24px',
                      background: 'white',
                      color: '#0369a1',
                      border: '1px solid #0369a1',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '15px'
                    }}
                  >
                    Log In
                  </button>
                </>
              )}
              <button
                onClick={() => setShowLocalPrompt(false)}
                style={{
                  padding: '8px',
                  background: 'none',
                  color: '#64748b',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventionAssessmentPage;